import React from 'react';
import { PropTypes } from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';

/**
 * Is this href pointing off-site? Shared so the Markdown renderer, the
 * notification reader and the notification page all answer it the same way.
 * Anything that isn't an absolute http(s) URL to another origin is treated as
 * internal — EXCEPT protocol-relative ("//host") and non-http schemes such as
 * `javascript:`, which are neither internal nor safe to link and are rejected by
 * isSafeHref below.
 */
const isExternalHref = (href = '') => (
  /^https?:\/\//i.test(href)
  && !(typeof window !== 'undefined' && href.startsWith(window.location.origin))
);

/**
 * Only site-relative paths and absolute http(s) URLs may be rendered as a link
 * target. Mirrors the server-side check on Messages.link_url — belt and braces,
 * since rows written before that validation existed are still in the table.
 */
const isSafeHref = (href = '') => {
  const url = String(href || '').trim();
  if (!url || url.startsWith('//')) return false;
  return url.startsWith('/') || /^https?:\/\//i.test(url);
};

/**
 * Link renderer for Markdown content (notifications, announcements, the tips page).
 *
 * Markdown has no syntax for "open in a new tab" vs "same tab", so we decide by
 * destination:
 *   - External (a full http/https URL to another site) → opens in a NEW tab,
 *     so the reader doesn't lose Doctivity.
 *   - Internal site path (/providers/123) → a router Link, so it navigates
 *     client-side instead of reloading the whole SPA. This matters more than it
 *     used to: Markdown is now the ONLY way a notification links anywhere, so
 *     these are the links people actually follow out of a notification.
 *   - Anything else (an anchor like #tips, a relative path, a same-origin
 *     absolute URL) → a plain anchor, which handles those correctly and keeps
 *     this safe on the standalone no-chrome pages.
 */
const SmartMarkdownLink = ({ href = '', children }) => {
  if (isExternalHref(href)) {
    return (
      <a href={href} target='_blank' rel='noopener noreferrer'>
        {children}
      </a>
    );
  }
  if (href && href.startsWith('/') && !href.startsWith('//')) {
    return <RouterLink to={href}>{children}</RouterLink>;
  }
  return <a href={href}>{children}</a>;
};

SmartMarkdownLink.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node,
};

export {
  SmartMarkdownLink,
  isExternalHref,
  isSafeHref,
};
export default SmartMarkdownLink;
